import PublicPage, { Section, Bullets } from "@/components/PublicPage";
import { COMPANY } from "@/lib/company";

export default function TermsOfService() {
  return (
    <PublicPage
      testId="terms-page"
      title="Terms of Service"
      intro={`These Terms govern your organisation's access to and use of the Plan-Sarthi platform operated by ${COMPANY.legalName}. By creating an account, starting a free trial or paying a subscription, you accept these Terms on behalf of your organisation.`}
    >
      <Section n={1} title="Who may use Plan-Sarthi">
        <p>
          Plan-Sarthi is a business-to-business platform. It is available only to registered companies, firms,
          partnerships or other legal entities engaged in engineering, procurement, construction, consultancy or
          related infrastructure work, and to their authorised employees and contractors.
        </p>
        <Bullets
          items={[
            "You must be at least 18 years old and authorised to bind your organisation to these Terms.",
            "You must register with a genuine work email address, and verify it before the workspace opens.",
            "Personal, anonymous or disposable-email registrations may be refused or removed.",
            "Each named user must have their own login. Sharing credentials between individuals is not permitted.",
            "Your organisation is responsible for everything done under its accounts, including by its contractors.",
          ]}
        />
      </Section>

      <Section n={2} title="Free trial">
        <Bullets
          items={[
            "New verified accounts receive a 14-day free trial, counted from the moment the email address is verified.",
            "No payment details are required to start the trial.",
            "The trial includes the full planning and execution suite: projects, WBS and quantities, DPR, tiered planning, CPM, hindrances, resources, EVM costing, what-if modelling, store and procurement, employee KPIs and the export centre.",
            "AI modules are NOT included in the trial. The Risk Radar, AI risk narratives and the AI BOQ Parser require a paid plan, and no AI credits are granted during the trial.",
            "When the 14 days end the workspace locks. Your data is retained and becomes accessible again the moment you upgrade.",
            "One trial per organisation. Creating repeat accounts to extend a trial is a breach of these Terms.",
          ]}
        />
      </Section>

      <Section n={3} title="Subscriptions, fees and payment">
        <Bullets
          items={[
            "All prices are quoted and charged in Indian Rupees (INR) and are exclusive of GST unless stated otherwise. Applicable taxes are added at checkout.",
            "Payments are processed by Razorpay. We do not see or store your card, netbanking or UPI credentials.",
            "Monthly plans are billed for a 30-day period from the date of a successful payment. Per-project licences are one-time charges tied to a single project package. AI credit packs are one-time purchases.",
            "Entitlements are activated only after the payment signature is verified server-side. A failed or unverified payment grants no access.",
            "Subscriptions do not auto-renew silently: access continues until the paid period ends and you renew by making a further payment.",
            "You may stop using the platform at any time; simply do not renew. Fees already paid for the current period are not refunded except as set out in clause 4.",
            "We may revise pricing with at least 30 days' notice by email. Existing paid periods are honoured at the old price.",
          ]}
        />
      </Section>

      <Section n={4} title="Refunds">
        <p>
          Because access is granted immediately on payment, subscription fees are generally non-refundable. We will,
          however, refund in full where a duplicate payment was captured, where a technical failure on our side
          prevented you from using a paid plan for more than five consecutive working days and we could not resolve
          it, or where the law requires a refund. Requests must be sent to {COMPANY.email} within 7 days of the
          charge. Approved refunds are returned through Razorpay to the original payment instrument, typically within
          7–10 working days. Unused AI credits are not refundable.
        </p>
      </Section>

      <Section n={5} title="Acceptable use">
        <p>You agree not to:</p>
        <Bullets
          items={[
            "Upload unlawful, infringing, defamatory or malicious content, or any code intended to damage the platform.",
            "Attempt to breach, probe or circumvent authentication, authorisation, role permissions, trial limits or payment verification.",
            "Reverse engineer, decompile, scrape, resell, sublicense or white-label the platform, or use it to build a competing product.",
            "Use the platform to store data unrelated to project execution, or personal data you have no right to process.",
            "Overload the service with automated requests, or exceed fair-use limits on file uploads (30 MB per BOQ file) and API calls.",
            "Misrepresent your identity, your organisation, or your authority to act for it.",
          ]}
        />
        <p>
          We may suspend or terminate access immediately, without refund, for a material breach of this clause, and
          may report unlawful activity to the authorities.
        </p>
      </Section>

      <Section n={6} title="Intellectual property">
        <p>
          <strong>Your data stays yours.</strong> You retain all right, title and interest in the projects, WBS and
          BOQ items, quantities, rates, DPRs, photographs, reports and any other content your organisation uploads or
          generates ("Customer Data"). You grant us a limited, non-exclusive licence to host, process, transmit and
          display Customer Data solely to operate the platform for you, to provide support, and to create backups.
        </p>
        <p>
          We retain all right, title and interest in the Plan-Sarthi platform itself, including its source code,
          engines (CPM, EVM, S-curve, what-if, forecasting), interface design, documentation, name, logo and branding.
          Nothing in these Terms transfers any of it to you. You may use exported reports freely within your business
          and with your clients.
        </p>
        <p>
          We may publish anonymised, aggregated statistics about platform usage that cannot identify you, your
          organisation or any project.
        </p>
      </Section>

      <Section n={7} title="Availability and support">
        <p>
          We aim for high availability but do not guarantee uninterrupted service. Planned maintenance is notified in
          advance where practical. Support is provided by email at {COMPANY.email} on working days, 9:30 AM – 6:30 PM
          IST. We do not commit to a contractual service level unless a separate written agreement says so.
        </p>
      </Section>

      <Section n={8} title="Advisory nature of outputs">
        <p>
          All forecasts, risk bands, health scores, critical-path results, cost projections and AI-generated narratives
          are decision-support outputs computed from the data you enter. They are advisory only. See the Disclaimer,
          which forms part of these Terms.
        </p>
      </Section>

      <Section n={9} title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, {COMPANY.legalName} shall not be liable for any indirect,
          incidental, special, punitive or consequential loss, nor for loss of profit, revenue, contracts, goodwill,
          anticipated savings, or for project delay, cost overrun, liquidated damages, penalty or claim arising from
          decisions taken using the platform, or for loss or corruption of data not caused by our gross negligence.
        </p>
        <p>
          Our total aggregate liability arising out of or in connection with the platform, whether in contract, tort
          or otherwise, shall not exceed the total fees actually paid by you to us in the three months immediately
          preceding the event giving rise to the claim. Where no fees have been paid — for example during a free trial
          — the platform is provided "as is" and our liability is limited to INR 1,000.
        </p>
        <p>
          Nothing in these Terms excludes liability that cannot lawfully be excluded, including for fraud or wilful
          misconduct.
        </p>
      </Section>

      <Section n={10} title="Indemnity">
        <p>
          You agree to indemnify and hold us harmless against claims, losses and reasonable legal costs arising from
          your Customer Data, your breach of these Terms or of applicable law, or your unauthorised use of third-party
          material through the platform.
        </p>
      </Section>

      <Section n={11} title="Suspension, termination and data on exit">
        <p>
          You may close your account at any time by writing to {COMPANY.email}. We may suspend or terminate on
          non-payment, on a material breach, or if required by law. On termination you may export your data for 30
          days, after which it is deleted in line with the Privacy Policy. Clauses on intellectual property,
          liability, indemnity and governing law survive termination.
        </p>
      </Section>

      <Section n={12} title="Confidentiality">
        <p>
          Each party will keep the other's non-public information confidential and use it only for the purpose of this
          relationship. We treat your project data, rates and commercial figures as confidential.
        </p>
      </Section>

      <Section n={13} title="Changes to these Terms">
        <p>
          We may amend these Terms. Material changes will be notified by email or in-app at least 14 days before they
          take effect. Continued use after that date constitutes acceptance. If you do not agree, stop using the
          platform and write to us before the change takes effect.
        </p>
      </Section>

      <Section n={14} title="Governing law and jurisdiction">
        <p>
          These Terms are governed by the laws of India. The parties will first attempt to resolve any dispute
          amicably in good faith for 30 days. Failing that, the courts at {COMPANY.jurisdiction} shall have exclusive
          jurisdiction. This agreement is executed electronically and is valid under the Information Technology Act,
          2000.
        </p>
        <p>
          Contact: <strong>{COMPANY.email}</strong> · {COMPANY.legalName}, {COMPANY.address}.
        </p>
      </Section>
    </PublicPage>
  );
}
